import { useCallback, useRef } from 'react';
import {
  IconStatusCorrect,
  Stack,
  Text,
  useToast,
} from '@adc/parallax-component-library';

import { useDepartmentCreateMutation } from '~/api/query/departmentCreate';
import { useDepartmentUpdateMutation } from '~/api/query/departmentUpdate';
import { useIDNGet } from '~/api/query/idnGet';
import type { DepartmentDetails } from '~/api/types';
import {
  DepartmentForm,
  type DepartmentFormRef,
  type DepartmentFormValues,
} from '~/components/forms/department/departmentForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { DOMAINS } from '~/features/navigation/domains';
import { useDomainName } from '~/features/navigation/useDomainName';
import { buildTestId } from '~/utils/buildTestId';

type DepartmentFormModalProps = {
  testID: string;
  idnId?: string;
  department?: DepartmentDetails;
  handleAddDepartmentAdmin: (departmentId: string) => Promise<void>;
  handleViewDepartmentProfile: (departmentId: string) => Promise<void>;
};

export const DepartmentFormModal = ({
  testID,
  idnId,
  department,
  handleAddDepartmentAdmin,
  handleViewDepartmentProfile,
}: DepartmentFormModalProps) => {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const domain = useDomainName();
  const formRef = useRef<DepartmentFormRef>(null);
  const isEditing = Boolean(department);

  const { data: idnData } = useIDNGet(idnId);
  const { mutateAsync: createDepartment } = useDepartmentCreateMutation();
  const { mutateAsync: updateDepartment } = useDepartmentUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: DepartmentFormValues) => {
      if (isEditing) {
        await updateDepartment(values);

        closeModal('DepartmentForm');

        showToast({
          message: 'Department profile updated',
          'aria-label': 'Department profile updated',
          icon: <IconStatusCorrect accentColor="$support.positive.default" />,
        });
      } else {
        await createDepartment(values);

        closeModal('DepartmentForm');

        openModal('DepartmentAdded', {
          departmentName: values.name,
          idnName: values.idnName ?? idnData?.name,
          handleAddDepartmentAdmin: async () => {
            await handleAddDepartmentAdmin(values.id);
          },
          handleViewDepartmentProfile: async () => {
            await handleViewDepartmentProfile(values.id);
          },
          testID: 'department-added-modal',
        });
      }
    },
    [
      openModal,
      closeModal,
      isEditing,
      showToast,
      idnData?.name,
      handleAddDepartmentAdmin,
      handleViewDepartmentProfile,
      createDepartment,
      updateDepartment,
    ],
  );

  return (
    <Modal
      testID={testID}
      title={
        isEditing
          ? `Edit Department - ${department?.name}`
          : domain === DOMAINS.USER_ADMINISTRATION
            ? 'Add Department'
            : `Add Department to IDN ${department?.idn.name ? `- ${department.idn.name}` : (idnData?.name ?? '')}`
      }
      hasCloseButton
      size="m"
      primaryAction={{
        label: isEditing ? 'Save Changes' : 'Add department',
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('DepartmentForm');
        },
      }}
      onOpenChange={() => {
        closeModal('DepartmentForm');
      }}
    >
      {domain === DOMAINS.USER_ADMINISTRATION && (
        <Text>
          The information below will be displayed when inviting patients to
          connect and share their data. Ensure your department address is
          correct for verification.
        </Text>
      )}
      <Stack paddingTop={domain !== DOMAINS.USER_ADMINISTRATION ? '$2' : '$0'}>
        <DepartmentForm
          ref={formRef}
          testID={buildTestId({
            testID,
            suffix: 'form',
          })}
          onSubmit={onFormSubmit}
          hiddenFields={
            domain === DOMAINS.USER_ADMINISTRATION
              ? ['idnName', 'idnId']
              : undefined
          }
          defaultValues={
            isEditing
              ? department
                ? {
                    idnId: department.idn.id,
                    idnName: department.idn.name,
                    id: department.id,
                    careTeamId: department.careTeamId,
                    name: department.name,
                    phone: department.phone,
                    address: department.address.mainLine,
                    addressLine2: department.address.secondaryLine,
                    state: department.address.state,
                    city: department.address.city,
                    country: department.address.country,
                    zipCode: department.address.zipCode,
                  }
                : undefined
              : {
                  idnId,
                  ...(idnData?.name ? { idnName: idnData.name } : {}),
                }
          }
        />
      </Stack>
    </Modal>
  );
};
